import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const DynamicAdBlock = ({ adConfig }) => {
  const adRef = useRef(null);

  const { type, content, link, enabled } = adConfig || {};

  // Handle Google AdSense logic
  useEffect(() => {
    if (enabled && type === 'adsense' && content && adRef.current) {
      // Clear previous injections safely
      adRef.current.innerHTML = '';

      // Match all script tags (src and inline)
      const doc = new DOMParser().parseFromString(content, 'text/html');
      
      // Inject non-script HTML content (e.g. ins tag)
      const nonScripts = Array.from(doc.body.childNodes).filter(
        node => node.tagName && node.tagName.toLowerCase() !== 'script'
      );
      
      nonScripts.forEach(node => {
        adRef.current.appendChild(node.cloneNode(true));
      });

      // Inject scripts to execute
      const scripts = Array.from(doc.querySelectorAll('script'));
      
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        if (script.src) {
          newScript.src = script.src;
          newScript.async = true;
          // Note: In AdSense, duplicate core JS is handled by window.adsbygoogle automatically
        } else {
          newScript.textContent = script.textContent;
        }
        adRef.current.appendChild(newScript);
      });
    }
  }, [type, content, enabled]);

  if (!adConfig || !enabled) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-7xl mx-auto px-4 my-10 flex justify-center w-full"
    >
      {type === 'image' ? (
        link ? (
          <a 
            href={link} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="block w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-sm border border-stone-200 hover:shadow-lg hover:border-stone-300 transition-all text-center"
          >
            {content ? (
              <img 
                src={content} 
                alt="Advertisement" 
                className="w-full h-auto object-cover max-h-[300px]" 
                loading="lazy" 
              />
            ) : (
               <div className="w-full h-32 bg-stone-100 flex items-center justify-center text-stone-400 font-bold uppercase tracking-widest text-sm border-2 border-dashed border-stone-300 rounded-xl">
                 Ad Space Available
               </div>
            )}
          </a>
        ) : (
          <div 
            className="block w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-sm border border-stone-200 text-center"
          >
            {content ? (
              <img 
                src={content} 
                alt="Advertisement" 
                className="w-full h-auto object-cover max-h-[300px]" 
                loading="lazy" 
              />
            ) : (
               <div className="w-full h-32 bg-stone-100 flex items-center justify-center text-stone-400 font-bold uppercase tracking-widest text-sm border-2 border-dashed border-stone-300 rounded-xl">
                 Ad Space Available
               </div>
            )}
          </div>
        )
      ) : (
        <div ref={adRef} className="w-full max-w-4xl mx-auto flex justify-center text-center overflow-hidden bg-stone-50 rounded-xl" />
      )}
    </motion.div>
  );
};

export default DynamicAdBlock;
